import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Chip,
  CircularProgress,
  Pagination as MuiPagination,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import BusinessIcon from '@mui/icons-material/Business';
import VerifiedIcon from '@mui/icons-material/Verified';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ListPageLayout from '../components/shared/list-page-layout';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { TableSkeleton } from '../components/shared/skeletons';
import { IListPurchaseContractsRequest } from '../../model/base/request/list-purchase-contracts-request';
import { usePurchaseContractsController } from './context/purchase-contracts-controller';
import { usePurchaseContractsState } from './context/purchase-contracts-context';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";

export function PurchaseContractsComponent() {
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadData } = usePurchaseContractsController();
  const { contracts, loading } = usePurchaseContractsState();
  const [currentPage, setCurrentPage] = useState(1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filters, setFilters] = useState<IListPurchaseContractsRequest>({
    page: 0,
    pageSize: 12,
    term: '',
    startDate: '',
    endDate: '',
    isCovid19: undefined,
  });

  useEffect(() => {
    loadData(filters);
  }, []);

  const filterFields: FilterField[] = [
    {
      name: 'term',
      label: 'Pesquisar',
      type: 'text',
      placeholder: 'Buscar por empresa ou processo',
      fullWidth: false,
      gridSize: { xs: 12, md: 3 },
    },
    {
      name: 'isCovid19',
      label: 'Tipo',
      type: 'select',
      options: [
        { value: 1, label: 'COVID-19' },
        { value: 0, label: 'Normal' },
      ],
      placeholder: 'Selecione o tipo',
      gridSize: { xs: 12, md: 3 },
    },
    {
      name: 'startDate',
      label: 'Data Inicial',
      type: 'date',
      gridSize: { xs: 12, md: 3 },
    },
    {
      name: 'endDate',
      label: 'Data Final',
      type: 'date',
      gridSize: { xs: 12, md: 3 },
    },
  ];

  const handleViewDetails = (contract: any) => {
    if (contract?.externalUrl) {
      window.open(contract.externalUrl, '_blank', 'noopener,noreferrer');
    } else {
      navigate(`/contratos-aquisicoes/detalhes/${contract?.id}`);
    }
  };

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    const searchFilters = { ...filters, page: 0 };
    setCurrentPage(1);
    setFilters(searchFilters);
    loadData(searchFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: IListPurchaseContractsRequest = {
      page: 0,
      pageSize: 12,
      term: '',
      startDate: '',
      endDate: '',
      isCovid19: undefined,
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    loadData(clearedFilters);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    const newFilters = { ...filters, page: page - 1 };
    setFilters(newFilters);
    loadData(newFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDocument = (document?: string) => {
    if (!document) return '-';
    // Formatar CNPJ: 00.000.000/0000-00
    if (document.length === 14) {
      return document.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    return document;
  };

  return (
    <ListPageLayout
      title="Contratos e Aquisições"
      subtitle="Consulte os contratos e aquisições do município"
      breadcrumbs={[
        { label: 'Contratos e Aquisições' },
      ]}
      filters={
        <ListFilters
          fields={filterFields.map((field) => ({
            ...field,
            value: filters[field.name as keyof IListPurchaseContractsRequest],
          }))}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={loading}
        />
      }
    >
      {loading && !contracts.hasItems() ? (
        <TableSkeleton rows={12} columns={6} hasActions={true} />
      ) : !contracts.hasItems() ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nenhum contrato encontrado com os filtros aplicados.
        </Alert>
      ) : (
        <>
          <Box sx={{ position: 'relative' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1000,
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={60} />
              </Box>
            )}
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {contracts.items.map((contract) => (
                  <Paper key={contract.id} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }} onClick={() => handleViewDetails(contract)}>
                    <Box
                      sx={{
                        backgroundColor: 'primary.main',
                        px: 2,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <BusinessIcon sx={{ fontSize: 24, color: 'white' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'white', flex: 1 }}>
                        {contract.processNumber || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                        {contract.isCovid19 && (
                          <Chip icon={<VerifiedIcon />} label="COVID-19" size="small" color="warning" />
                        )}
                        {contract.externalUrl && (
                          <Chip icon={<VerifiedIcon />} label="PNCP" size="small" color="success" />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                        Empresa Contratada
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {contract.contractedCompanyName || '-'}
                      </Typography>
                      {contract.contractedCompanyDocument && (
                        <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 1 }}>
                          CNPJ: {formatDocument(contract.contractedCompanyDocument)}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                        Prazo
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {formatDate(contract.contractDeadline)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                        Valor Pago
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        {formatCurrency(contract.amountPaid)}
                      </Typography>
                      {contract.balance > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Saldo: {formatCurrency(contract.balance)}
                        </Typography>
                      )}
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Tooltip title={contract.externalUrl ? "Abrir no PNCP" : "Ver Detalhes"} arrow>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={(e) => { e.stopPropagation(); handleViewDetails(contract); }}
                          sx={{ '&:hover': { backgroundColor: 'info.main', color: 'white' } }}
                        >
                          {contract.externalUrl ? <OpenInNewIcon /> : <InfoIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
            <TableContainer
              component={Paper}
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                opacity: loading ? 0.5 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: 'primary.main',
                    }}
                  >
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '60px',
                      }}
                    >
                      <DescriptionIcon sx={{ verticalAlign: 'middle' }} />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '15%',
                      }}
                    >
                      Processo
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                      }}
                    >
                      Empresa Contratada
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '12%',
                      }}
                    >
                      Prazo
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '15%',
                      }}
                    >
                      Valor Pago
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '80px',
                        textAlign: 'center',
                      }}
                    >
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contracts.items.map((contract) => (
                    <TableRow
                      key={contract.id}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: 'action.hover',
                        },
                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                        transition: 'background-color 0.2s ease',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleViewDetails(contract)}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <BusinessIcon
                            sx={{
                              fontSize: 32,
                              color: 'primary.main',
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {contract.processNumber || '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          {contract.isCovid19 && (
                            <Chip
                              icon={<VerifiedIcon />}
                              label="COVID-19"
                              size="small"
                              color="warning"
                            />
                          )}
                          {contract.externalUrl && (
                            <Chip
                              icon={<VerifiedIcon />}
                              label="PNCP"
                              size="small"
                              color="success"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.5,
                          }}
                        >
                          {contract.contractedCompanyName || '-'}
                        </Typography>
                        {contract.contractedCompanyDocument && (
                          <Typography variant="caption" color="text.disabled" display="block">
                            CNPJ: {formatDocument(contract.contractedCompanyDocument)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(contract.contractDeadline)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          {formatCurrency(contract.amountPaid)}
                        </Typography>
                        {contract.balance > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Saldo: {formatCurrency(contract.balance)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'center',
                          }}
                        >
                          <Tooltip title={contract.externalUrl ? "Abrir no PNCP" : "Ver Detalhes"} arrow>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleViewDetails(contract)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'info.main',
                                  color: 'white',
                                },
                              }}
                            >
                              {contract.externalUrl ? <OpenInNewIcon /> : <InfoIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </Box>

          {contracts.pageCount > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 2,
              }}
            >
              <MuiPagination
                count={contracts.pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                disabled={loading}
              />
            </Box>
          )}
        </>
      )}
    </ListPageLayout>
  );
}
